import { useState, useEffect, useCallback } from "react";

import { formatDistanceToNow } from "date-fns";
import { AlertTriangle, CheckCircle, Loader2, Database, Play, RefreshCw } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "@/components/ui/sonner";
import { useEdgeFunction } from "@/hooks/useEdgeFunction";
import { supabase } from "@/integrations/supabase/client";

const MIGRATABLE_TABLES = [
  'workspaces', 'boards', 'personas', 'folders', 'projects', 'tags', 'project_tags', 
  'sessions', 'messages', 'memories', 'memory_history', 'message_bookmarks', 
  'prebuilt_boards', 'convention_packs', 'session_frameworks', 'activity_goals', 
  'activity_streaks', 'user_gamification', 'user_achievements', 'user_preferences', 
  'email_preferences'
];

interface Checkpoint {
  table_name: string;
  last_migrated_at: string;
  last_migration_duration_ms: number;
  last_migration_record_count: number;
  updated_at: string;
}

interface MigrationLog {
  table: string;
  message: string;
  type: 'info' | 'success' | 'error';
  timestamp: string;
}

const DataMigrationManager = () => {
  const [checkpoints, setCheckpoints] = useState<Map<string, Checkpoint>>(new Map());
  const [selectedTables, setSelectedTables] = useState<Set<string>>(new Set(MIGRATABLE_TABLES));
  const [isMigrating, setIsMigrating] = useState(false);
  const [logs, setLogs] = useState<MigrationLog[]>([]);
  const [loadingCheckpoints, setLoadingCheckpoints] = useState(true);
  const { execute: runMigration, isLoading } = useEdgeFunction({ auth: true });

  const fetchCheckpoints = useCallback(async () => {
    setLoadingCheckpoints(true);
    const { data, error } = await supabase.from('migration_checkpoints').select('*');
    if (error) {
      toast.error('Failed to load migration checkpoints.');
      console.error(error);
    } else {
      const map = new Map<string, Checkpoint>();
      data.forEach(c => map.set(c.table_name, c));
      setCheckpoints(map);
    }
    setLoadingCheckpoints(false);
  }, []);

  useEffect(() => {
    fetchCheckpoints();
  }, [fetchCheckpoints]);

  const handleToggleTable = (tableName: string) => {
    setSelectedTables(prev => {
      const next = new Set(prev);
      if (next.has(tableName)) {
        next.delete(tableName);
      } else {
        next.add(tableName);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedTables.size === MIGRATABLE_TABLES.length) {
      setSelectedTables(new Set());
    } else {
      setSelectedTables(new Set(MIGRATABLE_TABLES));
    }
  };

  const startMigration = async (fullResync: boolean) => {
    if (selectedTables.size === 0) {
      toast.error("Please select at least one table to migrate.");
      return;
    }

    setIsMigrating(true);
    setLogs([{ table: 'System', message: `Starting migration for ${selectedTables.size} tables...`, type: 'info', timestamp: new Date().toISOString() }]);

    const { data, error } = await runMigration('migrate-data', {
      body: {
        tables: Array.from(selectedTables),
        fullResync,
      },
    });

    if (error) {
      toast.error("Migration failed. Check logs for details.");
      setLogs(prev => [...prev, { table: 'System', message: `Migration failed: ${error.message}`, type: 'error', timestamp: new Date().toISOString() }]);
    } else {
      const newLogs: MigrationLog[] = data.results.map((r: any) => ({
        table: r.table,
        message: r.message || (r.status === 'success' ? `Migrated ${r.count} records in ${r.duration}ms.` : 'Unknown error'),
        type: r.status === 'success' ? 'success' : 'error',
        timestamp: new Date().toISOString(),
      }));
      setLogs(prev => [...prev, ...newLogs]);
      toast.success("Migration process finished.");
      fetchCheckpoints();
    }
    
    setIsMigrating(false);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Data Migration
          </CardTitle>
          <CardDescription>
            Migrate data from this Supabase project to a target project.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive" className="mb-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Warning: Experimental Feature</AlertTitle>
            <AlertDescription>
              This tool directly manipulates databases. Ensure your target database schema is up-to-date. Always back up data before running a full resync.
            </AlertDescription>
          </Alert>
          <div className="flex gap-4">
            <Button onClick={() => startMigration(false)} disabled={isLoading}>
              <Play className="h-4 w-4 mr-2" />
              {isLoading ? 'Migrating...' : 'Run Delta Sync'}
            </Button>
            <Button onClick={() => startMigration(true)} variant="outline" disabled={isLoading}>
              <RefreshCw className="h-4 w-4 mr-2" />
              {isLoading ? 'Migrating...' : 'Run Full Resync'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Select Tables to Migrate</CardTitle>
            <div className="flex items-center space-x-2 mt-2">
              <Checkbox id="select-all" checked={selectedTables.size === MIGRATABLE_TABLES.length} onCheckedChange={handleSelectAll} />
              <Label htmlFor="select-all">Select All</Label>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-96">
              <div className="space-y-2">
                {MIGRATABLE_TABLES.map(table => (
                  <div key={table} className="flex items-center space-x-2">
                    <Checkbox
                      id={table}
                      checked={selectedTables.has(table)}
                      onCheckedChange={() => handleToggleTable(table)}
                    />
                    <Label htmlFor={table} className="font-mono text-sm">{table}</Label>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Migration Logs</CardTitle>
            <CardDescription>Real-time progress of the migration.</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-96 bg-muted rounded-md p-4">
              {isLoading && logs.length <= 1 && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Waiting for migration to start...</span>
                </div>
              )}
              <div className="space-y-2 text-xs font-mono">
                {logs.map((log, i) => (
                  <div key={i} className={`flex gap-2 ${log.type === 'error' ? 'text-destructive' : log.type === 'success' ? 'text-green-600' : ''}`}>
                    <span className="text-muted-foreground">{new Date(log.timestamp).toLocaleTimeString()}</span>
                    <span className="font-bold">[{log.table}]</span>
                    <span>{log.message}</span>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Migration Checkpoints</CardTitle>
          <CardDescription>Status of the last migration for each table.</CardDescription>
        </CardHeader>
        <CardContent>
          {loadingCheckpoints ? (
            <Loader2 className="h-6 w-6 animate-spin" />
          ) : (
            <ScrollArea className="h-64">
              <div className="space-y-2">
                {MIGRATABLE_TABLES.map(table => {
                  const checkpoint = checkpoints.get(table);
                  return (
                    <div key={table} className="flex items-center justify-between p-2 border rounded-md">
                      <span className="font-mono text-sm">{table}</span>
                      {checkpoint ? (
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span>Last run: {formatDistanceToNow(new Date(checkpoint.updated_at), { addSuffix: true })}</span>
                          <span>{checkpoint.last_migration_record_count} records in {checkpoint.last_migration_duration_ms}ms</span>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">Never migrated</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DataMigrationManager;
